<template>
  <section class="fmc" :class="{ 'fmc--quiet': quiet }" :data-test="testId" :data-channel="channel" :aria-label="title + ' message'">
    <header class="fmc-head">
      <span class="fmc-icon" :class="{ 'fmc-icon--on': !quiet }" aria-hidden="true">
        <i class="fa" :class="isEmail ? 'fa-envelope-o' : 'fa-commenting-o'"></i>
      </span>
      <span class="fmc-title" :data-test="testId + '-title'">{{ title }}</span>
      <span v-if="tagText" class="fmc-tag" :data-test="testId + '-tag'">{{ tagText }}</span>
      <span class="fmc-spacer"></span>
      <button
        type="button"
        class="fmc-switch"
        :class="{ 'fmc-switch--on': value.on }"
        role="switch"
        :aria-checked="value.on ? 'true' : 'false'"
        :aria-label="title + ' on or off'"
        :data-test="testId + '-toggle'"
        @click="toggle"
      ><span class="fmc-knob"></span></button>
    </header>

    <div class="fmc-body">
      <slot v-if="mode === 'template'" name="template"></slot>
      <template v-else>
        <message-template-box
          :value="value.body"
          :label="isEmail ? 'Email' : 'Text message'"
          :slot-key="slotKey"
          :channel="channel"
          :subject="isEmail ? value.subject : ''"
          :default-subject="defaultSubject"
          :subject-error="subjectError"
          :default-text="defaultText"
          :chips="chips"
          :error="bodyError"
          :link="link"
          :preview-site="previewSite"
          :sample-vars="sampleVars"
          :sample-preview="false"
          @input="onBody"
          @update:subject="onSubject"
          @link-pick="onLinkPick"
        />
        <slot name="footer"></slot>
      </template>
    </div>
  </section>
</template>

<script>
import MessageTemplateBox from '@/components/MessageTemplateBox.vue';
import { findUnknownVariables } from '@/utils/templateVariables';
import { REMINDER_SAMPLE_VARS } from '@/utils/reminderSampleVars';
import { validateMessage, MESSAGE_ERROR_TEXT } from '@/utils/formsSettings/formLink';
import { CHIP_NAMES, buildLinkItems } from '@/utils/formsSettings/editorModel';

/**
 * ONE message card of Forms Settings — "Text" or "Email" (board MessageCard.dc.html; plan docs/plans/2026-10-20-forms-settings-plan.md §1f, §2.1):
 * a header with the channel icon, the title, an "Off" / "Not sending" tag and the on / off switch, then the message box (MessageTemplateBox with the
 * Form link chip) and what the owning tab slots in under it. Used by New Patients (everyone + every rule) and Medical History Update.
 *
 * CONTROLLED. `value` = { on:Boolean, body, subject? (email), formLink:{kind,id,name}|null }; every edit emits ONE `input` with the whole next
 * object. The card keeps no copy and never changes `on` by itself except from its own switch. (A tab maps this to its stored shape: editorModel
 * toCardMessage / fromCardMessage for New Patients, the slot's `inline` for Medical History.)
 *
 * VALIDATION is WP0's validateMessage and nothing else, mapped onto the box: the body's first problem is the box's `error` (a blank body's own
 * "Reset to default or write your own." is the box's), the subject's first problem its `subjectError`; an unknown {{variable}} is left to the box's
 * own flag + "Did you mean" fix whenever the box flags it. LINK_NOT_PICKED is not an error line: the box's readout says "no form picked yet · Choose a
 * form". A message that is off is never validated. The errors show live (the board does the same); a tab's validate() only has to reveal the rest.
 *
 * `dim` = "Not sending" (a workflow already sends these patients their forms): the body is dimmed and tagged, but still editable (an office can
 * prepare it). An OFF message is dimmed and tagged "Off" the same way — state never depends on contrast alone.
 *
 * `mode` 'template' replaces the box with the `template` slot (Medical History's wiki-page dropdown); in 'box' mode the `footer` slot sits under it.
 */
// The box adds "Reset to default or write your own." under a blank field itself, so the WP0 sentence goes in without its own copy of that tail.
const BLANK_BODY = MESSAGE_ERROR_TEXT.BODY_BLANK.replace(/\s*Reset to default or write your own\.$/, '');
const BLANK_SUBJECT = MESSAGE_ERROR_TEXT.SUBJECT_BLANK;

export default {
  name: 'PfeFormsMessageCard',
  components: { MessageTemplateBox },
  props: {
    channel: { type: String, required: true, validator: (v) => v === 'sms' || v === 'email' },
    value: { type: Object, required: true },
    defaultText: { type: String, default: '' },
    defaultSubject: { type: String, default: '' },
    /** The site's forms [{ formIDForWeb, name, formType }] and packets [{ packetID, name, formIDs }] — the Form link picker's rows. */
    forms: { type: Array, default: () => [] },
    packets: { type: Array, default: () => [] },
    /** The forms / packets read failed on the server: the lists are empty because of that, so a saved choice is never called "deleted". */
    catalogFailed: { type: Boolean, default: false },
    /** { name, phone, signatureEnabled } — the real practice, for the email box's own preview. */
    site: { type: Object, default: () => ({}) },
    dim: { type: Boolean, default: false },
    mode: { type: String, default: 'box', validator: (v) => v === 'box' || v === 'template' },
    /** data-test of the card; the toggle / tag / title are '<testId>-toggle' etc. */
    testId: { type: String, default: 'pfe-msg' },
    slotKey: { type: String, default: '' },
  },
  computed: {
    isEmail() { return this.channel === 'email'; },
    title() { return this.isEmail ? 'Email' : 'Text'; },
    quiet() { return this.dim || !this.value.on; },
    tagText() {
      if (this.dim) return 'Not sending';
      return this.value.on ? '' : 'Off';
    },
    chips() { return CHIP_NAMES; },
    previewSite() {
      const site = this.site || {};
      return { name: site.name || '', phone: site.phone || '', hasSignature: !!site.signatureEnabled };
    },
    // The counter measures a message with a sample patient; the practice's name and phone are the real ones (they change the length).
    sampleVars() {
      const site = this.site || {};
      return Object.assign({}, REMINDER_SAMPLE_VARS, site.name ? { site_name: site.name } : {}, site.phone ? { site_officephone: site.phone } : {});
    },
    link() {
      return {
        token: 'form_link',
        chipLabel: 'Form link',
        required: true,
        title: 'Which form should this link open?',
        searchPlaceholder: 'Search forms and packets…',
        items: buildLinkItems(this.forms, this.packets),
        itemsLoaded: !this.catalogFailed,
        itemsLoading: false,
        value: this.value.formLink || null,
      };
    },
    problems() {
      return validateMessage({
        on: this.value.on ? 'TRUE' : 'FALSE',
        body: this.value.body,
        subject: this.value.subject,
        formLink: this.value.formLink,
      }, this.channel);
    },
    bodyError() { return this.errorFor('body', this.value.body, BLANK_BODY, { allowFormLink: true }); },
    subjectError() { return this.isEmail ? this.errorFor('subject', this.value.subject, BLANK_SUBJECT, {}) : ''; },
  },
  methods: {
    errorFor(field, text, blankSentence, unknownOpts) {
      const mine = this.problems.filter((p) => p.field === field);
      const first = mine.find((p) => p.code !== 'UNKNOWN_VARIABLE');
      if (first) return first.code === 'BODY_BLANK' || first.code === 'SUBJECT_BLANK' ? blankSentence : first.message;
      const unknown = mine.find((p) => p.code === 'UNKNOWN_VARIABLE');
      // the box flags an unknown {{variable}} itself (with a one-click fix); only a name it would let through is said here
      if (unknown && findUnknownVariables(text, unknownOpts).length === 0) return unknown.message;
      return '';
    },
    emit(patch) {
      this.$emit('input', Object.assign({}, this.value, patch));
    },
    toggle() { this.emit({ on: !this.value.on }); },
    onBody(body) { this.emit({ body }); },
    onSubject(subject) { this.emit({ subject }); },
    onLinkPick(formLink) { this.emit({ formLink: { kind: formLink.kind, id: formLink.id, name: formLink.name } }); },
  },
};
</script>

<style lang="scss" scoped>
@import './editor';

.fmc {
  min-width: 0;
  background: $pp-surface;
  border: 1px solid $pp-border;
  border-radius: 14px;
  box-shadow: $pp-shadow;
}
.fmc-head {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  border-bottom: 1px solid $pfe-neutral-100;
}
.fmc-icon {
  width: 30px;
  height: 30px;
  flex: none;
  border-radius: 8px;
  background: $pfe-neutral-50;
  color: $pfe-neutral-400;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;

  &--on { background: $pp-brand-subtle; color: $pp-brand; }
}
.fmc-title { font-size: 15px; font-weight: 800; letter-spacing: -0.01em; }
.fmc-tag {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 999px;
  background: $pfe-neutral-50;
  border: 1px solid $pp-border;
  font-size: 11.5px;
  font-weight: 600;
  color: $pfe-neutral-600;
  white-space: nowrap;
}
.fmc-spacer { flex: 1; }

.fmc-switch {
  position: relative;
  flex: none;
  width: 40px;
  height: 23px;
  padding: 0;
  border: 0;
  border-radius: 999px;
  background: $pp-border-strong;
  cursor: pointer;
  transition: background 0.15s ease;

  &--on { background: $pp-brand; }
  &:focus-visible { outline: 2px solid $pp-brand; outline-offset: 2px; }
}
.fmc-knob {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 19px;
  height: 19px;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 1px 3px rgba(12, 15, 14, 0.25);
  transition: transform 0.15s ease;

  .fmc-switch--on & { transform: translateX(17px); }
}

.fmc-body {
  padding: 16px 18px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  transition: opacity 0.15s ease;

  .fmc--quiet & { opacity: 0.55; }
}
</style>
